/**
 * /venues/:venueSlug/tournaments/:id/match/:matchId — the full scoring record of
 * one match.
 *
 * The end of the drill-down, and the only screen in the app that shows a finished
 * match visit by visit. Until Stage 4 that record was reachable only over the
 * tournament room's socket, and only for the match live on a board right now.
 *
 * ?from=:entryId IS THE BACK LINK. A match has two players, so a summary route
 * scoped to one of them cannot be guessed from the match alone — the summary page
 * appends the card you came from. Without it the link falls back to the
 * tournament's public results, which is a real page rather than a dead end.
 *
 * REMAINING SCORES ARE THE SERVER'S ARITHMETIC. Each visit carries its own
 * remaining_score, re-derived by getMatchLegs rather than read off whatever was
 * stored, so a corrected visit shows a leg that adds up. Nothing is recomputed
 * here.
 */

import { Link, useParams, useSearchParams } from 'react-router-dom';
import { apiGet } from '../lib/api.js';
import { useVenuePage } from '../lib/useVenuePage.js';
import { formatAvg } from '../lib/format.js';
import { matchFormatLabel } from '../lib/placement.js';

/** The seven figures worth comparing side by side. Legs are in the score line. */
const COMPARISON = [
  { label: 'Average',   read: (p) => formatAvg(p.threeDartAvg) },
  { label: 'First 9',   read: (p) => formatAvg(p.firstNineAvg) },
  { label: '100+',      read: (p) => p.count100Plus },
  { label: '140+',      read: (p) => p.count140Plus },
  { label: '180s',      read: (p) => p.count180s },
  { label: 'High out',  read: (p) => p.highestCheckout },
  { label: 'Breaks',    read: (p) => p.breaksOfThrow },
];

/**
 * One visit cell: what was scored, and what it left.
 *
 * A bust keeps its darts and scores nothing — calculateRemainingScore is explicit
 * that a declared bust stays a bust — so it is shown as such rather than as a 0
 * somebody might read as a missed visit.
 *
 * The checkout visit is the one that left nothing, and it carries its dart count
 * because finishing 40 in two darts is a different thing from finishing it in
 * three.
 */
function Visit({ visit }) {
  if (!visit) return <td />;

  const checkout = visit.remaining_score === 0;

  return (
    <td className={checkout ? 'hd-visit-win' : visit.is_bust ? 'hd-visit-bust' : undefined}>
      <span className="hd-visit-score">{visit.is_bust ? '0' : visit.score}</span>
      {visit.is_bust === 1 && <span className="hd-muted"> bust</span>}
      {checkout
        ? <span className="hd-muted"> · {visit.darts_used} dart{visit.darts_used === 1 ? '' : 's'}</span>
        : <span className="hd-muted"> · {visit.remaining_score}</span>}
    </td>
  );
}

/**
 * One leg, as a three-column table: player 1's visits, the visit number, player
 * 2's visits.
 *
 * Rows are indexed by VISIT NUMBER rather than by array position, because the two
 * players do not throw the same number of times — whoever checks out has thrown
 * one more or one fewer than their opponent, and pairing by position would slide
 * the columns out of step from the first uneven leg onwards.
 */
function Leg({ leg, legNumber, player1, player2, nameFor }) {
  const visitsOf = (side) =>
    (leg.visits ?? []).filter((v) => v.player_id === side?.entryId);

  const p1 = visitsOf(player1);
  const p2 = visitsOf(player2);

  const rows = Math.max(
    ...p1.map((v) => v.visit_number),
    ...p2.map((v) => v.visit_number),
    0
  );

  const at = (visits, n) => visits.find((v) => v.visit_number === n) ?? null;

  const winner = leg.winner_id != null ? nameFor(leg.winner_id) : null;

  return (
    <div className="hd-leg">
      <div className="hd-leg-label">
        Leg {legNumber}
        {winner && <> · {winner}</>}
        {/* checkout_score is what was left when the winning dart was thrown, so
            it is the finish — not the score of the visit. */}
        {leg.checkout_score != null && <>, {leg.checkout_score} checkout</>}
        {!winner && <> · in progress</>}
      </div>

      <table className="hd-table hd-leg-table">
        <thead>
          <tr>
            <th>{player1?.displayName ?? '—'}</th>
            <th className="hd-leg-num">#</th>
            <th>{player2?.displayName ?? '—'}</th>
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }, (_, i) => i + 1).map((n) => (
            <tr key={n}>
              <Visit visit={at(p1, n)} />
              <td className="hd-leg-num hd-muted">{n}</td>
              <Visit visit={at(p2, n)} />
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function MatchDetail() {
  const { venueSlug, id, matchId } = useParams();
  const [search] = useSearchParams();
  const from = search.get('from');

  const state = useVenuePage(
    venueSlug,
    (venue) => apiGet(
      `/api/venues/${venue.id}/tournaments/${id}/match/${matchId}/detail`
    ),
    [id, matchId]
  );

  if (state.status === 'loading') {
    return <div className="hd-empty">Loading…</div>;
  }

  if (state.status === 'error') {
    return (
      <div className="hd-stack hd-stats-page">
        <h1 className="hd-page-title">
          {state.venue ? 'Match unavailable' : 'Venue not found'}
        </h1>
        <p className="hd-muted">
          {state.venue ? state.error : <>No venue at <code>/{venueSlug}</code>.</>}
          {' '}
          <Link to={state.venue ? `/venues/${venueSlug}/tournaments` : '/'}>
            {state.venue ? 'Back to the tournaments' : 'Back to the home page'}
          </Link>
        </p>
      </div>
    );
  }

  const { venue, data } = state;
  const { match, legsWon, stats, legs } = data;
  const { player1, player2 } = match;

  const backTo = from
    ? `/venues/${venue.slug}/tournaments/${id}/player/${from}`
    : `/venues/${venue.slug}/tournaments/${id}/results`;

  const byEntry = new Map((stats?.players ?? []).map((p) => [p.entryId, p]));
  const nameFor = (entryId) =>
    [player1, player2].find((p) => p?.entryId === entryId)?.displayName ?? null;

  const scoreOf = (side) => (side ? legsWon?.[side.entryId] ?? 0 : 0);

  return (
    <div className="hd-stack hd-stats-page">
      <p className="hd-meta">
        <Link to={backTo}>{from ? '← Back to the tournament' : '← Back to the results'}</Link>
      </p>

      <div className="hd-stats-head">
        <p>
          <span className="hd-badge hd-badge-neutral">{matchFormatLabel(match)}</span>
          {match.isLive && <span className="hd-badge hd-badge-accent">Live</span>}
        </p>

        <h1 className="hd-page-title">
          {player1?.displayName ?? '—'}
          <span className="hd-match-vs"> {scoreOf(player1)}–{scoreOf(player2)} </span>
          {player2?.displayName ?? '—'}
        </h1>

        <p className="hd-meta">
          <Link to={`/venues/${venue.slug}/tournaments`}>{venue.name}</Link>
          {' · '}First to {match.legsToWin}
          {match.boardNumber != null && <> · Board {match.boardNumber}</>}
          {match.marker?.displayName && <> · marked by {match.marker.displayName}</>}
        </p>
      </div>

      <section className="hd-stats-section">
        <h2 className="hd-section-title">Comparison</h2>

        {/* stats is nullable by contract, and a forfeited match counts no darts —
            so this is a note rather than a table of zeros. */}
        {!stats?.players?.length ? (
          <p className="hd-muted">No figures recorded for this match.</p>
        ) : (
          <table className="hd-table hd-compare-table">
            <thead>
              <tr>
                <th>{player1?.displayName ?? '—'}</th>
                <th className="hd-compare-label" />
                <th className="hd-table-num">{player2?.displayName ?? '—'}</th>
              </tr>
            </thead>
            <tbody>
              {COMPARISON.map(({ label, read }) => {
                const a = byEntry.get(player1?.entryId);
                const b = byEntry.get(player2?.entryId);

                return (
                  <tr key={label}>
                    <td>{a ? read(a) : '—'}</td>
                    <td className="hd-compare-label hd-muted">{label}</td>
                    <td className="hd-table-num">{b ? read(b) : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      <section className="hd-stats-section">
        <h2 className="hd-section-title">Legs</h2>

        {/* A match can be completed with no legs recorded — a bye, or data that
            was seeded or corrected by hand. matches.js calls that missing data
            rather than a 0–0 result, and so does this. */}
        {!legs?.length ? (
          <div className="hd-empty">No scoring recorded for this match.</div>
        ) : (
          legs.map((leg) => (
            <Leg
              key={leg.id}
              leg={leg}
              legNumber={leg.leg_number}
              player1={player1}
              player2={player2}
              nameFor={nameFor}
            />
          ))
        )}
      </section>
    </div>
  );
}
